package com.appsyde.shared.modules.ticket.domain.attachment

/**
 * Règles de validation des pièces jointes outbound (V1).
 * Utilisé côté client pour validation avant envoi ; le backend applique les mêmes règles (source de vérité).
 *
 * Règle de priorité : MIME connu et autorisé → OK ; sinon fallback extension (allowlist) ; blocklist → refus.
 * Le MIME envoyé par le navigateur peut être vide ou générique.
 */
object OutboundAttachmentRules {

    const val MAX_TOTAL_BYTES = 20L * 1024 * 1024  // 20 MB
    const val MAX_SINGLE_FILE_BYTES = 10L * 1024 * 1024  // 10 MB

    /** Types MIME autorisés (allowlist). */
    val ALLOWED_MIME_TYPES: Set<String> = setOf(
        "application/pdf",
        "image/png",
        "image/jpeg",
        "image/webp",
        "text/plain",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",  // docx
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",       // xlsx
        "application/vnd.openxmlformats-officedocument.presentationml.presentation", // pptx
        "application/zip"
    ).map { it.lowercase() }.toSet()

    /** Extensions autorisées (fallback quand MIME vide/générique). */
    val ALLOWED_EXTENSIONS: Set<String> = setOf(
        "pdf", "png", "jpg", "jpeg", "webp", "txt",
        "docx", "xlsx", "pptx", "zip"
    ).map { it.lowercase() }.toSet()

    /** Extensions dangereuses → refus direct. */
    val BLOCKED_EXTENSIONS: Set<String> = setOf(
        "exe", "msi", "bat", "cmd", "sh", "ps1", "jar", "apk", "dmg",
        "docm", "xlsm", "html", "htm"
    ).map { it.lowercase() }.toSet()

    fun extensionFromFilename(filename: String): String? {
        val lastDot = filename.lastIndexOf('.')
        if (lastDot < 0 || lastDot == filename.length - 1) return null
        return filename.substring(lastDot + 1).lowercase()
    }

    /**
     * Valide type (MIME + extension) et tailles.
     * @return null si OK, sinon message d'erreur.
     */
    fun validateForAdd(
        mimeType: String?,
        filename: String,
        sizeBytes: Long,
        currentTotalBytes: Long,
        currentCount: Int
    ): String? {
        val ext = extensionFromFilename(filename)
        if (ext != null && BLOCKED_EXTENSIONS.contains(ext)) {
            return "Type de fichier non autorisé : $filename"
        }
        val mime = (mimeType?.trim()?.lowercase()).takeIf { !it.isNullOrBlank() }
        val mimeOk = mime != null && ALLOWED_MIME_TYPES.contains(mime)
        val extOk = ext != null && ALLOWED_EXTENSIONS.contains(ext)
        if (!mimeOk && !extOk) {
            return "Type de fichier non autorisé : $filename"
        }
        if (sizeBytes > MAX_SINGLE_FILE_BYTES) {
            return "Fichier trop volumineux : $filename (max ${MAX_SINGLE_FILE_BYTES / (1024 * 1024)} MB)"
        }
        val newTotal = currentTotalBytes + sizeBytes
        if (newTotal > MAX_TOTAL_BYTES) {
            return "Limite totale dépassée (${newTotal / (1024 * 1024)} MB > ${MAX_TOTAL_BYTES / (1024 * 1024)} MB)"
        }
        return null
    }
}
